import React from 'react';

import {
  AccessTime as AccessTimeIcon,
  AttachMoney as AttachMoneyIcon,
  Badge as BadgeIcon,
  Business as BusinessIcon,
  ChevronRight as ChevronRightIcon,
  Description as DescriptionIcon,
  InfoOutlined as InfoOutlinedIcon,
  Place as PlaceIcon,
  Shield as ShieldIcon,
  Work as WorkIcon,
} from '@mui/icons-material';
import { Avatar, Box, ButtonBase, Chip, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { isNotNil } from 'es-toolkit';

import { Link } from '@hrlogics/react-components';

import {
  appRoutes,
  DateFormatPatterns,
  DEFAULT_VALUE_IF_NIL,
  FEIN_MASK,
  RouterEntitiesId,
} from '@clearverify/constants';
import { formatDate, formatWithMask } from '@clearverify/utils';

import { EmployeeDataEmploymentRecordInfoFragment } from 'src/api';
import { SxProp } from 'src/types/mui';
import { getEmploymentStatusLabel } from 'src/utils/employee';
import { buildUrl } from 'src/utils/router';
import { formatMaskedSsnNumber } from 'src/utils/ssn';

type EmployeeDataEmploymentRecordCardProps = {
  employmentRecord: EmployeeDataEmploymentRecordInfoFragment;
  onSeeMore?: (companyId: string) => void;
  loading?: boolean;
  /** Employer-side roles (Employer, PEO, PEO Payroll Provider, employee) never see this indicator. */
  hideEmployerLiveIndicator?: boolean;
};

export const EmployeeDataEmploymentRecordCard: React.FC<EmployeeDataEmploymentRecordCardProps> = ({
  employmentRecord,
  onSeeMore,
  loading,
  hideEmployerLiveIndicator = false,
}) => {
  const employeeName = [employmentRecord.employeeFirstName, employmentRecord.employeeLastName]
    .filter(isNotNil)
    .join(' ');

  const employerLocationLabel = React.useMemo(() => {
    const locationName =
      employmentRecord.employerLocation ?? employmentRecord.employerRegisteredBusinessName;
    return locationName
      ? `${locationName}${employmentRecord.employerLocationAddress ? `, ${employmentRecord.employerLocationAddress}` : ''}`
      : DEFAULT_VALUE_IF_NIL;
  }, [employmentRecord]);

  const handleClick = onSeeMore
    ? () => !loading && onSeeMore(employmentRecord.companyId)
    : undefined;

  // Built on the server so a stale term date before a rehire reads "Present", as in the tooltip.
  const employmentPeriodLabel = employmentRecord.employmentMostRecentEmployment;

  // Two employment periods (Original + Most Recent) — mirrors EmploymentDataDetails,
  // which only shows the "Original Hire Date" row when it's present.
  const showEmploymentPeriodsTooltip =
    !!employmentPeriodLabel && !!employmentRecord.employmentOriginalHireDate;

  const cardBody = (
    <Paper variant="outlined" sx={cardContainerStyles}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2}>
        <Stack direction="row" alignItems="flex-start" gap={2} minWidth={0}>
          <Avatar
            src={employmentRecord.employerLogoUrl ?? undefined}
            alt={employmentRecord.employerDisplayName ?? undefined}
            sx={avatarStyles}
          >
            <BusinessIcon />
          </Avatar>

          <Box minWidth={0}>
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="h6" fontWeight={600} lineHeight={1.2} noWrap minWidth={0}>
                {employeeName || employmentRecord.employerEmployeeId || '—'}
              </Typography>

              <Chip
                size="small"
                label={getEmploymentStatusLabel(employmentRecord.employmentStatus)}
                color={
                  employmentRecord.isEmploymentActive && employmentRecord.employmentStatus
                    ? 'success'
                    : 'default'
                }
                variant={
                  employmentRecord.isEmploymentActive && employmentRecord.employmentStatus
                    ? 'filled'
                    : 'outlined'
                }
              />

              {!hideEmployerLiveIndicator && (
                <Tooltip
                  title={
                    employmentRecord.isEmployerLive
                      ? 'Live: employee can view this data and verifiers can request verifications for it.'
                      : 'Not Live: employee cannot view this data and verifiers cannot request verifications for it.'
                  }
                >
                  <Chip
                    size="small"
                    color={employmentRecord.isEmployerLive ? 'success' : 'warning'}
                    label={
                      employmentRecord.isEmployerLive ? 'Employer: Live' : 'Employer: Not Live'
                    }
                  />
                </Tooltip>
              )}
            </Stack>

            <Stack direction="row" alignItems="flex-start" gap={1} mt={0.5} minWidth={0}>
              <WorkIcon sx={iconStyles} fontSize="small" />
              <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                {employmentRecord.employeePositionTitle ?? '—'}
                {employmentPeriodLabel && ` | ${employmentPeriodLabel}`}
              </Typography>
              {showEmploymentPeriodsTooltip && (
                <Tooltip
                  title={
                    <React.Fragment>
                      <div>Original Hire Date: {employmentRecord.employmentOriginalHireDate}</div>
                      <div>
                        Most Recent Employment:{' '}
                        {employmentRecord.employmentMostRecentEmployment || DEFAULT_VALUE_IF_NIL}
                      </div>
                      <div>
                        Total time with Employer:{' '}
                        {employmentRecord.employmentTotalTimeWithEmployer || DEFAULT_VALUE_IF_NIL}
                      </div>
                    </React.Fragment>
                  }
                >
                  <InfoOutlinedIcon sx={iconStyles} fontSize="small" />
                </Tooltip>
              )}
            </Stack>
          </Box>
        </Stack>

        {handleClick && <ChevronRightIcon sx={chevronStyles} fontSize="small" />}
      </Stack>

      <Box sx={infoGridStyles}>
        <Stack gap={1}>
          <InfoRow
            icon={<BusinessIcon sx={iconStyles} fontSize="small" />}
            label="Employer"
            value={<EmployerInfoRowValueContent employmentRecord={employmentRecord} />}
          />
          <InfoRow
            icon={<BusinessIcon sx={iconStyles} fontSize="small" />}
            label="Reg. Business Name"
            value={employmentRecord.employerRegisteredBusinessName ?? DEFAULT_VALUE_IF_NIL}
          />
          <InfoRow
            icon={<DescriptionIcon sx={iconStyles} fontSize="small" />}
            label="FEIN"
            value={
              employmentRecord.employerFein
                ? formatWithMask(employmentRecord.employerFein, FEIN_MASK)
                : DEFAULT_VALUE_IF_NIL
            }
          />
          <InfoRow
            icon={<PlaceIcon sx={iconStyles} fontSize="small" />}
            label="Location"
            value={employerLocationLabel}
          />
        </Stack>

        <Stack gap={1}>
          <InfoRow
            icon={<ShieldIcon sx={iconStyles} fontSize="small" />}
            label="SSN / Date of Birth"
            value={`${formatMaskedSsnNumber(employmentRecord.employeeLast4OfSsn) || '—'} / ${formatShortDate(
              employmentRecord.employeeDateOfBirth,
            )}`}
          />
          <InfoRow
            icon={<BadgeIcon sx={iconStyles} fontSize="small" />}
            label="Employee ID"
            value={employmentRecord.employerEmployeeId ?? '—'}
          />
          <InfoRow
            icon={<AccessTimeIcon sx={iconStyles} fontSize="small" />}
            label="Avg Hours"
            value={employmentRecord.averageHoursPerPayPeriod ?? '—'}
          />
          <InfoRow
            icon={<AttachMoneyIcon sx={iconStyles} fontSize="small" />}
            label="Avg Base Pay"
            value={employmentRecord.averageBasePay ?? '—'}
          />
        </Stack>
      </Box>
    </Paper>
  );

  if (!handleClick) return cardBody;

  return (
    <ButtonBase onClick={handleClick} sx={buttonBaseStyles}>
      {cardBody}
    </ButtonBase>
  );
};

type InfoRowProps = {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
};

const InfoRow: React.FC<InfoRowProps> = ({ icon, label, value }) => (
  <Stack direction="row" alignItems="flex-start" gap={1} minWidth={0}>
    {icon}
    <Typography
      variant="body2"
      sx={{
        minWidth: 0,
        overflowWrap: 'anywhere',
        '& > .label': { color: 'text.secondary', mr: 0.5 },
      }}
    >
      <span className="label">{label}:</span>
      {value}
    </Typography>
  </Stack>
);

type EmployerInfoRowValueContentProps = {
  employmentRecord: EmployeeDataEmploymentRecordInfoFragment;
};

const EmployerInfoRowValueContent: React.FC<EmployerInfoRowValueContentProps> = ({
  employmentRecord,
}) => {
  const employerLink = employmentRecord.employerId
    ? buildUrl(appRoutes.employers.detail.companyInformation, {
        [RouterEntitiesId.employer]: employmentRecord.employerId,
      })
    : null;
  const employerPart = employmentRecord.employerDisplayName ? (
    employerLink ? (
      <Link
        to={employerLink}
        target="_blank"
        sx={employerLinkStyles}
        onClick={e => e.stopPropagation()}
      >
        {employmentRecord.employerDisplayName}
      </Link>
    ) : (
      employmentRecord.employerDisplayName
    )
  ) : null;

  const peoLink = employmentRecord.peoId
    ? buildUrl(appRoutes.peos.detail.companyInformation, {
        [RouterEntitiesId.peo]: employmentRecord.peoId,
      })
    : null;
  const peoPart = employmentRecord.peoDisplayName ? (
    peoLink ? (
      <Link to={peoLink} target="_blank" sx={employerLinkStyles} onClick={e => e.stopPropagation()}>
        {employmentRecord.peoDisplayName}
      </Link>
    ) : (
      employmentRecord.peoDisplayName
    )
  ) : null;

  if (employerPart && peoPart) {
    return (
      <React.Fragment>
        {employerPart} ({peoPart})
      </React.Fragment>
    );
  }

  return employerPart ?? peoPart ?? DEFAULT_VALUE_IF_NIL;
};

const formatShortDate = (value: string | Date | null | undefined): string => {
  if (!value) return DEFAULT_VALUE_IF_NIL;
  const iso = value instanceof Date ? value.toISOString() : value;
  return formatDate(iso, DateFormatPatterns.shortDateWithSlash) ?? DEFAULT_VALUE_IF_NIL;
};

const buttonBaseStyles: SxProp = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  borderRadius: '16px',
};

const cardContainerStyles: SxProp = theme => ({
  padding: 3,
  borderRadius: '16px',
  width: '100%',
  // Let the info grid below respond to the card's own width (it renders both
  // full-width and inside a narrow multi-match column), not the viewport.
  containerType: 'inline-size',
  transition: theme.transitions.create('box-shadow'),
  '&:hover': {
    boxShadow: theme.shadows[2],
  },
});

const avatarStyles: SxProp = theme => ({
  width: 56,
  height: 56,
  bgcolor: theme.palette.action.hover,
  color: theme.palette.text.secondary,
});

const iconStyles: SxProp = theme => ({
  color: theme.palette.primary.main,
  flexShrink: 0,
});

const chevronStyles: SxProp = theme => ({
  color: theme.palette.text.secondary,
  mt: 0.5,
});

const infoGridStyles: SxProp = {
  mt: 2,
  display: 'grid',
  // `minmax(0, 1fr)` lets tracks shrink below their content so long values can't
  // blow the grid out past the card. One column by default; two when the card is
  // wide enough (queried against the card's own inline size — see containerType).
  gridTemplateColumns: 'minmax(0, 1fr)',
  columnGap: 4,
  rowGap: 1,
  '@container (min-width: 34rem)': {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  },
};

const employerLinkStyles: SxProp = {
  textDecoration: 'underline',
};
